import { Building2, FileText, IdCard, Lightbulb } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import useTranslations from "@/shared/lang/useTranslations";

import type { CaptureStartResponse } from "../types/verification-api.types";
import { CapturePhotoGuide } from "./CapturePhotoGuide";
import {
  ProviderCaptureFrame,
  type ProviderCaptureHint,
  type ProviderCaptureMode,
  type ProviderCaptureState,
} from "./ProviderCaptureFrame";
import { ProviderCaptureSdk } from "./ProviderCaptureSdk";

export interface CaptureStepProps {
  /**
   * Requests the ephemeral SDK access token (`capture/start`) and resolves to the response. Frame-agnostic:
   * marco B passes the public call, marcos A/C pass the internal one.
   */
  start: () => Promise<CaptureStartResponse>;
  /** KYC capture uses a single embedded session (document + selfie), so the `selfie` mode is not used here. */
  mode: Exclude<ProviderCaptureMode, "selfie">;
  /** Advances the flow — fired by the SDK when the applicant submits the capture. */
  onComplete: () => void;
}

/**
 * Wraps `ProviderCaptureFrame` + the embedded capture SDK: requests the access token via `start`, mounts the
 * SDK in the frame slot, and reflects the lifecycle (loading → active → error). The provider widget drives
 * the capture and submission; the final verdict still arrives via the webhook (status polling / socket).
 *
 * The document capture opens on the photo guide (`CapturePhotoGuide`) and asks for the token only once the
 * person says they are ready, so no capture session is started for someone still changing their camera's
 * format. Every way into this step goes through it: the first time, a resumed capture and a retry after a
 * rejection. The company capture has no guide: its documents are not photos of an identity document.
 */
export function CaptureStep({ start, mode, onComplete }: CaptureStepProps) {
  const { t, activeLanguage } = useTranslations("verification");
  const [ready, setReady] = useState(mode === "company");
  const [state, setState] = useState<ProviderCaptureState>("loading");
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const startRef = useRef(start);
  useEffect(() => {
    startRef.current = start;
  });

  // Fetch the access token once the capture may start. setState runs only inside the promise callbacks
  // (async continuations).
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    startRef
      .current()
      .then((data) => {
        if (!cancelled) {
          setAccessToken(data.access_token);
          setState("active");
        }
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [ready]);

  const handleRetry = useCallback(() => {
    setState("loading");
    setAccessToken(null);
    startRef
      .current()
      .then((data) => {
        setAccessToken(data.access_token);
        setState("active");
      })
      .catch(() => setState("error"));
  }, []);

  // SDK token-expiration handler: fetch a fresh access token.
  const handleExpired = useCallback(async () => (await startRef.current()).access_token, []);

  const title = mode === "company" ? t("capture_company_title") : t("capture_doc_title");

  const instruction = mode === "company" ? t("capture_company_instruction") : t("capture_doc_instruction");

  const hints: ProviderCaptureHint[] =
    mode === "company"
      ? [
          { icon: Building2, label: t("capture_company_hint_incorporation") },
          { icon: IdCard, label: t("capture_company_hint_taxid") },
        ]
      : [
          { icon: Lightbulb, label: t("capture_doc_hint_light") },
          { icon: FileText, label: t("capture_doc_hint_complete") },
        ];

  if (!ready) return <CapturePhotoGuide onReady={() => setReady(true)} />;

  return (
    <ProviderCaptureFrame
      mode={mode}
      state={state}
      title={title}
      instruction={instruction}
      hints={hints}
      loadingLabel={t("capture_loading")}
      errorTitle={t("capture_error_title")}
      errorDescription={t("capture_error_description")}
      deniedTitle={t("capture_denied_title")}
      deniedDescription={t("capture_denied_description")}
      actionLabel={state === "denied" ? t("capture_action_allowed") : t("capture_action_retry")}
      onAction={handleRetry}
    >
      {state === "active" && accessToken ? (
        <ProviderCaptureSdk
          accessToken={accessToken}
          onExpired={handleExpired}
          onSubmitted={onComplete}
          onSdkError={() => setState("error")}
          lang={activeLanguage}
        />
      ) : null}
    </ProviderCaptureFrame>
  );
}
